// @flow
import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';
import _ from 'lodash';
 
import { type TRound } from '../../modules/types';

import * as selectors from '../../modules/selectors';
import * as actions from '../../modules/actions';

import TwoColumnLayoutWithSidebarWidgets from "../../components/TwoColumnLayoutWithSidebarWidgets";
import JsonFetcher from '../../components/utils/JsonFetcher';
import withRoundsSelector from '../../components/utils/withRoundsSelector';
import RoundSelector from '../../components/RoundSelector';
import AdsContainer from '../../components/AdsContainer';
import Ad from '../../components/Ad';
import PageTitle from '../../components/PageTitle';
import PageSubTitle from '../../components/PageSubTitle';
import Fixtures from '../../components/Fixtures';
import Ladder from '../../components/Ladder';
import { Tooltip } from "../../components";
import TopBar from '../../components/TopBar';
import WaiverOrderWidget from '../../components/WaiverOrderWidget';
import { ButtonPrimaryLink } from '../../components/ButtonPrimary';
import PremiersWinnersBanner from '../../components/PremiersWinnersBanner';

import {
	getDefaultLeagueRoute,
	getLeagueRoundAliases,
	getLeagueFinalFullNames,
} from '../../helpers/league';
import { isMatchingPathnames } from '../../utils/url';
import { getActualRound, isRoundComplete } from '../../helpers/rounds';

const PageSubTitleStyled = styled(PageSubTitle)`
	display: flex;
    justify-content: space-between;
    align-items: center;
`;

const LadderSpan = styled.span`
	padding-right: 10px;
`;

type Props = {
	selected_league_id: number,
	ladder_teams_by_id: Object,
	ladder_teams_ordered: Array<Object>,
	fetchLeague: typeof actions.leagueDraft.showDraftLeague,
	fetchLadder: typeof actions.leagueDraft.ladderDraftLeague,
	is_pending_show: boolean,
	is_pending_ladder: boolean,
	user_id: number,
	rounds_ordered: Array<Object>,
	rounds_by_id: Object,
	selected_round_id: number,
	setSelectedRoundId: Function,
	league: Object,
	has_assistant_coach: boolean,
	coach_players_by_id: Object,
	match: Object,
	location: {
		pathname: string
	},
	players: Object,
	rounds: Array<TRound>
}

type State = {
	rfa_order_widget_open: boolean
}

class LeagueOverviewPage extends React.Component<Props, State> {
	state = {
		rfa_order_widget_open: false
	};

	constructor(props) {
		super(props);

		_.bindAll(this, ['onToggleWaiverOrderWidget']);
	}

	componentDidMount() {
		const { selected_round_id } = this.props;

		this.fetchLeague();

		if (selected_round_id) {
			this.fetchLadder();
		}
	}

	componentDidUpdate(prev_props) {
		const { selected_league_id, selected_round_id } = this.props;

		if (
			selected_league_id &&
			selected_league_id !== prev_props.selected_league_id
		) {
			this.fetchLeague();
			this.fetchLadder();
		}
		else if (
			selected_round_id !== prev_props.selected_round_id
		) {
			this.fetchLadder();
		}

		if (
			selected_league_id !== prev_props.selected_league_id
		) {
			this.setSelectedRoundIdIfOutsideLeagueRounds();
		}
	}

	setSelectedRoundIdIfOutsideLeagueRounds() {
		const { setSelectedRoundId } = this.props;

		setSelectedRoundId(this.league_selected_round);
	}

	/**
	 * Sometimes the active round which is the selected one by deafult will fall outside
	 * the leagues allowed rounds, in this case we must set the selected round to one the
	 * league is playing
	 */
	get league_selected_round() {
		const { selected_round_id, league } = this.props;

		if (selected_round_id && league && !!this.league_rounds) {
			const start_round = _.first(this.league_rounds).id;
			const end_round = _.last(this.league_rounds).id;

			if ( selected_round_id < start_round) {
				return start_round;
			}
			else if (selected_round_id > end_round) {
				return end_round;
			}

			return selected_round_id;
		}

		return  null;
	}

	get league_current_round_fixtures() {
		const { league, selected_round_id } = this.props;
		return selected_round_id ?
			_.get(league, `fixture.${selected_round_id}`, []): [];
	}
  
	get league_next_round_fixtures() {
		const { league, selected_round_id } = this.props;
		return selected_round_id ? 
			_.get(league, `fixture.${selected_round_id + 1}`, []): [];
	}

	get is_league_bye_round() {
		const {
			rounds_by_id,
			league,
			selected_round_id
		} = this.props;

		if (rounds_by_id && selected_round_id && league) {
			const round = rounds_by_id[selected_round_id];
			const isBye = _.get(round, 'is_bye', false);
			return Boolean(league.bye_rounds) ? false : isBye;
		}

		return false;
	}

	get league_rounds() {
		const {
			rounds_ordered,
			league
		} = this.props;

		if (rounds_ordered && league && league.start_round && league.end_round) {
			const actual_round = getActualRound(rounds_ordered);

			return rounds_ordered.filter(round =>
				(round.id >= league.start_round && round.id <= league.end_round) &&
				(
					round.id === league.start_round ||
					isRoundComplete(round) ||
					(actual_round && round.id === actual_round.id)
				)
			);
		}

		return rounds_ordered;
	}

	fetchLadder() {
		const { fetchLadder, selected_league_id, selected_round_id } = this.props;
		fetchLadder({
			league_id: selected_league_id,
			round: selected_round_id || selected_round_id
		});
	}

	fetchLeague() {
		const { fetchLeague, selected_league_id } = this.props;
		fetchLeague({
			id: selected_league_id
		});
	}

	onToggleWaiverOrderWidget() {
		this.setState(state => ({
			rfa_order_widget_open: !state.rfa_order_widget_open
		}));
	}

	renderFixturesHeading() {
		const { league } = this.props;
		return (
			<PageSubTitleStyled>
				Fixtures
				{league && (
					<ButtonPrimaryLink to={`/league/${league.id}/fixtures`}>
						View full Fixtures
					</ButtonPrimaryLink>
				)}
			</PageSubTitleStyled>
		);
	}

	renderLadderHeading() {
		const { league } = this.props;
		return (
			<PageSubTitleStyled>
				<div>
					<LadderSpan>Ladder</LadderSpan>
					<Tooltip
						show_on_left={false}
						title={''}
						mobile_show_on_right={true}
						description={'This condensed version of the Ladder offers ' +
						'a look at all players in contention for the finals so far, ' +
						'as well as where you sit within your league ladder ' +
						'if you are outside of the finalists'}
					/>
				</div>
				{league && (
					<ButtonPrimaryLink to={`/league/${league.id}/ladder`}>
						View full Ladder
					</ButtonPrimaryLink>
				)}
			</PageSubTitleStyled>
		);
	}

	renderFixturesSection() {
		const {
			ladder_teams_by_id,
			league,
			rounds_by_id,
			has_assistant_coach,
			coach_players_by_id,
			selected_round_id,
			players,
			rounds
		} = this.props;

		return (
			<React.Fragment>
				{this.renderFixturesHeading()}
				<Fixtures
					compact
					league={league}
					ladder_teams_by_id={ladder_teams_by_id}
					rounds_by_id={rounds_by_id}
					rounds={rounds}
					has_assistant_coach={has_assistant_coach}
					coach_players_by_id={coach_players_by_id}
					selected_round_id={selected_round_id}
					is_league_bye_round={this.is_league_bye_round}
					league_current_round_fixtures={this.league_current_round_fixtures}
					players={players}
					show_finals_trophy
					is_league_overview_page
				/>
			</React.Fragment>
		);
	}

	renderLadderSection() {
		const {
			ladder_teams_ordered,
			ladder_teams_by_id,
			rounds_by_id,
			selected_round_id,
			user_id,
			league
		} = this.props;
		ladder_teams_ordered
			.sort((a,b) => a.rank_selected_round - b.rank_selected_round);
		return (
			<React.Fragment>
				{this.renderLadderHeading()}
				<Ladder
					compact
					user_id={user_id}
					league={league}
					ladder_teams_ordered={ladder_teams_ordered}
					ladder_teams_by_id={ladder_teams_by_id}
					rounds_by_id={rounds_by_id}
					selected_round_id={selected_round_id}
					league_current_round_fixtures={this.league_current_round_fixtures}
					league_next_round_fixtures={this.league_next_round_fixtures}
					show_finals_trophy
				/>
			</React.Fragment>
		);
	}

	renderMainSection() {
		const {
			league,
			selected_round_id, rounds_ordered,
		} = this.props;
		const title_round =  selected_round_id || '-';
		const title_league_name = league ? league.name : '-';
		const round_full_names = getLeagueFinalFullNames(league, rounds_ordered);
		const round_full_name = round_full_names[selected_round_id];

		return (
			<div>
				<PageTitle>
					{round_full_name || `Round ${title_round}`} Overview for {title_league_name}
				</PageTitle>
				{this.premiers_banner}
				{this.renderFixturesSection()}
				{this.renderLadderSection()}
			</div>
		);
	}

	get premiers_banner() {
		const {
			league,
			selected_round_id, rounds_by_id,
			ladder_teams_by_id,
		} = this.props;

		const final_round_id = _.get(league, 'end_round');
		const final_round = _.get(rounds_by_id, final_round_id);
		const top_team = ladder_teams_by_id[league.fixture_winner];

		const is_show_banner = final_round && top_team
			&& isRoundComplete(final_round)
			&& selected_round_id === final_round_id;

		if (!is_show_banner) {
			return null;
		}

		return  <PremiersWinnersBanner
			team={top_team.name}
			firstname={top_team.firstname}
			lastname={top_team.lastname}
		/>;
	}

	renderAside() {
		const { league } = this.props;

		 return (
			<div>
				{league && league.id ? (
					<WaiverOrderWidget
						league_id={league.id}
						onToggle={this.onToggleWaiverOrderWidget}
						widget_open={this.state.rfa_order_widget_open}
					/>
				) : null }
			</div>
		 );
	}

	render() {
		const {
			setSelectedRoundId,
			league,
			location,
			selected_round_id,
			rounds_ordered
		} = this.props;

		if (!_.isEmpty(league)) {
			const default_league_route = getDefaultLeagueRoute(league);

			if (
				default_league_route &&
				isMatchingPathnames(default_league_route, location.pathname) === false
			) {
				return <Redirect to={default_league_route} />;
			}
		}

		return (
			<React.Fragment>
				<JsonFetcher fetch={['rounds', 'coach_players', 'players']} interval={60000} />
				<TopBar league={league} selected_round_id={selected_round_id} />
				<RoundSelector
					selected={selected_round_id}
					rounds={this.league_rounds}
					handleClick={setSelectedRoundId}
					round_aliases={getLeagueRoundAliases(league, rounds_ordered)}
				/>

				<AdsContainer>
					<Ad
						adUnit='myleagues/overview'
						size='top'
					/>
				</AdsContainer>

				<TwoColumnLayoutWithSidebarWidgets sidebar_open={this.state.rfa_order_widget_open}>
					{this.renderMainSection()}
					{this.renderAside()}
				</TwoColumnLayoutWithSidebarWidgets>

				<AdsContainer>
					<Ad
						adUnit='myleagues/overview'
						size='bottom'
					/>
				</AdsContainer>
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state, props) => {
	const selected_league_id = props.match.params.league_id;

	return {
		selected_league_id,
		league: selectors.leagueDraft.getLeague(state, props),
		is_pending_show: selectors.leagueDraft.show.isPending(state),
		is_pending_ladder: selectors.leagueDraft.ladder.isPending(state),
		ladder_teams_ordered: selectors.orderedLadderCalculated(state, props),
		ladder_teams_by_id: selectors.leagueDraft.ladder.getById(state),
		user_id: selectors.getUser(state).id,
		has_assistant_coach: Boolean(selectors.getUser(state).assistant_coach),
		coach_players_by_id: state.players.coach_by_id,
		players: selectors.players.getExtendedPlayersById(state, props),
		rounds: selectors.rounds.getRoundsWithMatches(state)
	};
};

const mapDispatchToProps = {
	fetchLeague: actions.leagueDraft.showDraftLeague,
	fetchLadder: actions.leagueDraft.ladderDraftLeague
};

export const LeagueOverview = connect(
	mapStateToProps,
	mapDispatchToProps
)(LeagueOverviewPage);

export default withRoundsSelector(LeagueOverview);


// WEBPACK FOOTER //
// ./src/pages/league/LeagueOverview.js